import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Camera,
  Check,
  ChevronDown,
  Download,
  FileText,
  Image,
} from 'lucide-react';

import { apiUrl } from '../../../../redux/services/api';
import { useOpenPdf } from '../../../../hooks/useOpenPdf';

import { links } from '../../../../data/links';

const LANGS = ['EN', 'FR', 'RU'];
const LANG_CHECK_MS = 1600;
const PHOTOS_DONE_MS = 1800;

const sections = [
  { key: 'pdf', label: 'PDF', icon: FileText },
  { key: 'poster', label: 'Poster', icon: Image },
];

const headerClasses =
  'flex justify-center items-center gap-1.5 px-2 w-full h-[30px] bg-[#a2a7b2] hover:bg-[#959aa6] text-sm text-whiteColor';

export const DropdownDownload = ({
  realtyId,
  hasPhotos = false,
  setIsOpen = () => {},
}) => {
  const ref = useRef(null);
  const [openSections, setOpenSections] = useState({});
  const [picked, setPicked] = useState({});
  const [photosState, setPhotosState] = useState('idle');
  const [withMyContact, setWithMyContact] = useState(false);
  const openPdf = useOpenPdf();

  const toggleSection = key => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleLangClick = (sectionKey, lang) => {
    openPdf({
      kind: sectionKey,
      ids: [realtyId],
      lang: lang.toLowerCase(),
      details: withMyContact ? 'self' : 'none',
    });

    const pickedKey = sectionKey + lang;

    setPicked(prev => ({ ...prev, [pickedKey]: true }));
    setTimeout(
      () =>
        setPicked(prev => {
          const next = { ...prev };
          delete next[pickedKey];
          return next;
        }),
      LANG_CHECK_MS
    );
  };

  const handleDownloadPhotos = async () => {
    if (photosState !== 'idle') return;

    setPhotosState('busy');

    try {
      const response = await fetch(
        `${apiUrl}/${links.photos}/${realtyId}/download`
      );

      if (!response.ok) {
        throw new Error(
          response.status === 404
            ? 'No photos to download'
            : 'Photos download failed'
        );
      }

      const url = URL.createObjectURL(await response.blob());
      const anchor = document.createElement('a');

      anchor.href = url;
      anchor.download = `realty-${realtyId}-photos.zip`;
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);

      setPhotosState('done');
      setTimeout(() => setPhotosState('idle'), PHOTOS_DONE_MS);
    } catch (error) {
      toast.error(error.message || 'Photos download failed');
      setPhotosState('idle');
    }
  };

  useEffect(() => {
    const handleClickOutside = event => {
      if (ref.current && !ref.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = event => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [ref]);

  const photosLabel = {
    idle: 'Photos',
    busy: 'Preparing…',
    done: 'Done',
  }[photosState];

  return (
    <div
      ref={ref}
      onClick={e => e.stopPropagation()}
      className="absolute top-[-5px] left-1.5 flex flex-col w-[130px] bg-whiteColor z-[2] shadow-[0_4px_14px_rgba(0,0,0,0.18)]"
    >
      <label className="flex justify-center items-center gap-1.5 w-full h-8 text-xs text-[#222] cursor-pointer">
        <input
          type="checkbox"
          checked={withMyContact}
          onChange={e => setWithMyContact(e.target.checked)}
        />
        <span>My contact</span>
      </label>

      {sections.map(({ key, label, icon: Icon }, index) => (
        <div key={key}>
          <button
            type="button"
            className={`${headerClasses} ${index ? 'border-t border-whiteColor' : ''}`}
            onClick={() => toggleSection(key)}
          >
            <Icon size={14} />
            <span>{label}</span>
            <ChevronDown
              size={13}
              strokeWidth={2.4}
              className={`transition-transform duration-150 ${openSections[key] ? 'rotate-180' : ''}`}
            />
          </button>

          {openSections[key] &&
            LANGS.map(lang => (
              <button
                key={lang}
                type="button"
                className="flex justify-center items-center gap-[5px] w-full h-8 bg-whiteColor hover:bg-[#f0f1f4] text-sm text-[#222]"
                onClick={() => handleLangClick(key, lang)}
              >
                <span>{lang}</span>
                {picked[key + lang] && (
                  <Check
                    size={12}
                    strokeWidth={2.8}
                    color="#3a8a3a"
                  />
                )}
              </button>
            ))}
        </div>
      ))}

      <button
        type="button"
        disabled={!hasPhotos}
        title={hasPhotos ? undefined : 'No photos to download'}
        className={`${headerClasses} border-t border-whiteColor disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#a2a7b2] ${photosState === 'busy' ? 'cursor-wait' : ''}`}
        onClick={handleDownloadPhotos}
      >
        <Camera size={14} />
        <span>{photosLabel}</span>
        {photosState === 'idle' && (
          <Download
            size={13}
            strokeWidth={2.2}
          />
        )}
        {photosState === 'done' && (
          <Check
            size={13}
            strokeWidth={2.8}
          />
        )}
      </button>
    </div>
  );
};
